import { Avatar } from '@mui/material';

import { useScraping } from './hooks/useScraping';
import {
  Table,
  SearchPanel,
  Badge,
  Toggle,
  LabelDate,
  Container,
  MoreActions,
  Spinner,
} from '../../../components';
import { Button, Modal, ConfirmModal } from '../../../components';
import { Image } from '../../../components';
import TaskForm from './components/TaskForm';
import TaskInfoModal from './components/TaskInfoModal';
import { DownloadModal } from './components/DownloadModal';
import { UploadScreenshotModal } from './components/UploadScreenshotModal';
import { CheckIcon } from '../../../assets/icons';
import { ScraperFilter } from './components/ScraperFilter';
import ScrapingImageModal from './components/ScrapingImageModal';
import type { FileTypes } from '../../../services/scraper';
import type { ScreenshotTypes } from '../../../services/scraper';
import { ScrapingStatus } from './const';
import { isDeletableStatus } from './utils';
import { createScrapingActions } from './utils/actions';

export const ScrapingTab = () => {
  const {
    scrapingList,
    loading,
    reloading,
    isOpened,
    updating,
    filters,
    onOpen,
    onClose,
    setPage,
    setFilters,
    handleAddScrapingTask,
    handleDownloadScrapingTask,
    handleretryScrapingTask,
    agentsOptions,
    campaignOptions,
    selectedTaskIds,
    handleSelectTask,
    handleSelectAll,
    handleBulkScrapingTasksExport,
    bulkScrapingExport,
    exportableTaskCount,
    handleRefreshDemoClick,
    handleRefreshDemoTasks,
    refreshingDemo,
    refreshableDemoTaskCount,
    formKey,
    formValues,
    setFormValues,
    bulkUploadData,
    handleBulkUpload,
    handleClearBulkUpload,
    downloading,
    deletingScraping,
    isDeleteModalOpen,
    handleDeleteClick,
    handleConfirmDelete,
    handleCloseDeleteModal,
    isBulkDeleteModalOpen,
    handleBulkDeleteClick,
    handleConfirmBulkDelete,
    handleCloseBulkDeleteModal,
    selectedTask,
    isInfoModalOpen,
    handleInfoClick,
    handleCloseInfoModal,
    visitStats,
    isLoadingStats,
    errorTask,
    isErrorModalOpen,
    handleErrorClick,
    handleCloseErrorModal,
    isDownloadModalOpen,
    onOpenDownloadModal,
    onCloseDownloadModal,
    downloadModalTaskId,
    setDownloadModalTaskId,
    isUploadScreenshotModalOpen,
    onOpenUploadScreenshotModal,
    onCloseUploadScreenshotModal,
    uploadScreenshotTaskId,
    setUploadScreenshotTaskId,
    handleUploadScreenshot,
    uploadingScreenshot,
    statusMetrics,
    loadingStatusMetrics,
  } = useScraping();

  const inProgress = loading || reloading || updating;

  const { tasks, pagination } = scrapingList?.[0] || {};
  const deletableTasks =
    tasks?.filter(task => isDeletableStatus(task.status)) || [];

  const isAllSelected =
    deletableTasks.length > 0 &&
    selectedTaskIds.length === deletableTasks.length;

  return (
    <div className="p-4">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch mb-4 gap-4 lg:gap-md">
        <div className="flex flex-col gap-4 flex-1 min-w-0 lg:max-w-[calc(100%-26rem)] xl:max-w-[calc(100%-24rem)] 2xl:max-w-[calc(100%-22rem)]">
          <ScraperFilter
            values={filters ?? {}}
            onChange={setFilters}
            campaignOptions={campaignOptions}
          />
        </div>
        <div className="flex flex-col gap-4 lg:gap-4 lg:justify-between lg:flex-shrink-0 lg:w-auto lg:min-w-[24rem] xl:min-w-[22rem] 2xl:min-w-[20rem] w-full">
          <Container className="justify-end h-full">
            <div className="flex items-center gap-2 text-small">
              <span className="text-secondary">In Process:</span>
              <span className="font-semibold">
                {loadingStatusMetrics
                  ? '...'
                  : (statusMetrics?.in_process ?? 0)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-small">
              <span className="text-secondary">Success:</span>
              <span className="font-semibold text-green-600">
                {loadingStatusMetrics ? '...' : (statusMetrics?.success ?? 0)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-small">
              <span className="text-secondary">Failure:</span>
              <span className="font-semibold text-red-600">
                {loadingStatusMetrics ? '...' : (statusMetrics?.failure ?? 0)}
              </span>
            </div>
          </Container>
          <div className="flex flex-wrap gap-x-sm gap-y-sm">
            <Button
              label="Bulk Export"
              onClick={() => handleBulkScrapingTasksExport()}
              size="medium"
              type="contrast"
              disabled={exportableTaskCount === 0 || bulkScrapingExport}
              className={exportableTaskCount === 0 ? 'opacity-50' : ''}
              labelClassName="text-main"
            />
            <Button
              label="Delete"
              onClick={handleBulkDeleteClick}
              size="medium"
              type="contrast"
              disabled={selectedTaskIds.length === 0 || deletingScraping}
              className={selectedTaskIds.length === 0 ? 'opacity-50' : ''}
              labelClassName="text-red-600 hover:text-red-700"
            />
            <Button
              label="Refresh Demo"
              onClick={handleRefreshDemoClick}
              size="medium"
              type="contrast"
              disabled={refreshableDemoTaskCount === 0 || refreshingDemo}
              className={refreshableDemoTaskCount === 0 ? 'opacity-50' : ''}
              labelClassName="text-main"
            />
            <Button
              label="Add Task"
              onClick={() => {
                onOpen();
              }}
              size="medium"
            />
          </div>
        </div>
      </div>

      <Modal size="medium" open={isOpened} onClose={onClose} title="New Task">
        <TaskForm
          formId={formKey}
          onCancel={onClose}
          agentsOptions={agentsOptions}
          saving={inProgress}
          onSave={() => handleAddScrapingTask()}
          formValues={formValues}
          setFormValues={setFormValues}
          bulkUploadData={bulkUploadData}
          handleBulkUpload={handleBulkUpload}
          handleClearBulkUpload={handleClearBulkUpload}
        />
      </Modal>

      <ConfirmModal
        title="Delete Scraping Task"
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        confirmButtonLabel="Delete"
        confirmButtonClassName="w-full bg-dangerSecondary hover:bg-danger border border-dangerSecondary text-contrast"
      >
        <p className="text-medium">
          Are you sure you want to delete this scraping task? This action cannot
          be undone.
        </p>
      </ConfirmModal>

      <TaskInfoModal
        task={selectedTask}
        open={isInfoModalOpen}
        onClose={handleCloseInfoModal}
        visitStats={visitStats}
        isLoadingStats={isLoadingStats}
      />

      <ConfirmModal
        title="Delete Scraping Tasks"
        open={isBulkDeleteModalOpen}
        onClose={handleCloseBulkDeleteModal}
        onConfirm={handleConfirmBulkDelete}
        confirmButtonLabel="Delete"
        confirmButtonClassName="w-full bg-dangerSecondary hover:bg-danger border border-dangerSecondary text-contrast"
        saving={deletingScraping}
      >
        <p className="text-medium">
          Are you sure you want to delete {selectedTaskIds.length} scraping task
          {selectedTaskIds.length !== 1 ? 's' : ''}? This action cannot be
          undone.
        </p>
      </ConfirmModal>

      <Modal
        size="small"
        open={isErrorModalOpen}
        onClose={handleCloseErrorModal}
        title="Error Details"
      >
        <div className="p-4">
          <p className="text-medium whitespace-pre-wrap break-words">
            {(errorTask as any)?.error || 'No error message available'}
          </p>
        </div>
      </Modal>

      <DownloadModal
        open={isDownloadModalOpen}
        onClose={() => {
          onCloseDownloadModal();
          setDownloadModalTaskId(null);
        }}
        onDownload={(fileType: FileTypes) => {
          if (downloadModalTaskId) {
            handleDownloadScrapingTask(downloadModalTaskId, fileType);
          }
        }}
        downloading={downloading}
      />
      <UploadScreenshotModal
        open={isUploadScreenshotModalOpen}
        onClose={() => {
          onCloseUploadScreenshotModal();
          setUploadScreenshotTaskId(null);
        }}
        onUpload={(file: File, screenshotType: ScreenshotTypes) => {
          if (uploadScreenshotTaskId) {
            handleUploadScreenshot(
              uploadScreenshotTaskId,
              file,
              screenshotType
            );
          }
        }}
        uploading={uploadingScreenshot}
      />

      <div className="overflow-x-auto">
        <Table
          data={tasks}
          loading={loading}
          emptyMessage="There are no Scraping tasks yet."
          config={{
            columns: [
              {
                align: 'right',
                header: (
                  <Toggle
                    variant="checkbox"
                    value={isAllSelected}
                    onChange={() => handleSelectAll(tasks || [])}
                    disabled={deletableTasks.length === 0}
                    checkboxClassName="appearance-none w-6 h-6 rounded border-2 border-main bg-select checked:bg-main checked:border-main focus:ring focus:ring-main/20 transition-colors duration-150 cursor-pointer checkbox-simple"
                  />
                ),
                cellClassName: 'min-w-[5rem] max-w-[6rem] text-rightІ',
                getValue: task => (
                  <Toggle
                    variant="checkbox"
                    value={selectedTaskIds.includes(task.id)}
                    onChange={() =>
                      handleSelectTask(task.id, task.status || '')
                    }
                    disabled={!isDeletableStatus(task.status)}
                    checkboxClassName="appearance-none w-6 h-6 rounded border-2 border-main bg-select checked:bg-main checked:border-main focus:ring focus:ring-main/20 transition-colors duration-150 cursor-pointer checkbox-simple"
                  />
                ),
              },
              {
                sortName: 'url',
                header: 'URL',
                cellClassName: 'min-w-[5rem] max-w-[25rem] text-left',
                getValue: ({ url }) => <div className="truncate">{url}</div>,
              },
              {
                header: 'Screenshot',
                cellClassName: 'min-w-[6rem] text-left',
                getValue: item => (
                  <ScrapingImageModal imageUrl={item?.screenshot_url || ''}>
                    <Image src={item?.screenshot_url || ''} />
                  </ScrapingImageModal>
                ),
              },
              {
                header: 'Campaign Name',
                cellClassName:
                  'min-w-[5rem] max-w-[25rem] text-center truncate',
                getValue: ({ campaign_name }) => (
                  <span className="truncate text-center">
                    {campaign_name || '-'}
                  </span>
                ),
              },
              {
                header: 'Company Name',
                cellClassName: 'min-w-[5rem] max-w-[25rem] text-left truncate',
                getValue: ({ company_name }) => <span>{company_name}</span>,
              },
              {
                header: 'Demo Bot',
                cellClassName: 'min-w-[5rem] text-left',
                getValue: ({ should_create_demo_bot }) => (
                  <CheckIcon
                    color={should_create_demo_bot ? '#5861FA' : '#dbe0e5'}
                    width={20}
                    height={20}
                  />
                ),
              },
              {
                header: 'Requested by',
                sortName: 'requested_by',
                cellClassName: 'min-w-[5rem] text-left',
                getValue: ({ requested_by }) => (
                  <div className="flex items-center">
                    <Avatar
                      sx={{
                        bgcolor: '#3b82f6',
                        width: 28,
                        height: 28,
                        fontSize: '1.5rem',
                        mr: 1,
                      }}
                    >
                      {requested_by?.[0]}
                    </Avatar>
                    <span>{requested_by || 'Unknown'}</span>
                  </div>
                ),
              },
              {
                header: 'Date & Time',
                sortName: 'created_at',
                cellClassName: 'min-w-[5rem] text-left',
                getValue: ({ created_at }) => (
                  <LabelDate
                    date={created_at || 'N/A'}
                    showTime
                    formatDate="dd/MMM/yyyy"
                  />
                ),
              },
              {
                header: 'Status',
                sortName: 'status',
                cellClassName: 'min-w-[5rem] text-left',
                getValue: ({ status }) => <Badge status={status} />,
              },
              {
                header: 'Actions',
                cellClassName: 'min-w-[5rem]',
                getValue: task => {
                  const isInProgress = [
                    ScrapingStatus.SUBMITTED,
                    ScrapingStatus.PENDING,
                    ScrapingStatus.IN_PROCESS,
                    ScrapingStatus.STARTED,
                    ScrapingStatus.IN_PROCESS_PARSING,
                    ScrapingStatus.IN_PROCESS_KNOWLEDGE,
                    ScrapingStatus.IN_PROCESS_DEMO,
                  ].includes(task.status as ScrapingStatus);

                  const actions = createScrapingActions({
                    status: task.status,
                    onInfo: () => handleInfoClick(task),
                    onDownload: () => {
                      setDownloadModalTaskId(task.id || null);
                      onOpenDownloadModal();
                    },
                    onRetry: () => handleretryScrapingTask(task.id),
                    onViewErrors: () => handleErrorClick(task),
                    onDelete: () => handleDeleteClick(task.id || ''),
                    onRefreshDemo: () => {
                      if (task.id) {
                        handleRefreshDemoTasks([task.id], null);
                      }
                    },
                    onUploadScreenshot: () => {
                      setUploadScreenshotTaskId(task.id || null);
                      onOpenUploadScreenshotModal();
                    },
                    downloading,
                    error: (task as any).error,
                  });

                  return (
                    <div className="flex items-center justify-end h-full min-h-[3rem] gap-2">
                      {isInProgress && <Spinner className="h-10 w-10" />}
                      {actions.length > 0 && <MoreActions options={actions} />}
                    </div>
                  );
                },
              },
            ],
          }}
        />
      </div>
      <div className="mt-sm flex justify-between items-center">
        <SearchPanel
          totalItems={pagination?.total_items}
          page={pagination?.page}
          perPage={10}
          withPaginate
          setPage={setPage}
        />
      </div>
    </div>
  );
};
